import { useEffect, useRef, useState } from "react";

/** How long to wait before forcing content visible regardless of the observer. */
const FAILSAFE_MS = 1200;

export type UseRevealResult<T extends HTMLElement> = {
  ref: React.RefObject<T | null>;
  shown: boolean;
};

/**
 * Scroll-entrance state with a hard guarantee that content is never left hidden.
 *
 * Three deliberate properties, carried over from the Datum `Reveal` contract:
 *
 * 1. Starts `shown`. If IntersectionObserver is missing or the effect never
 *    runs, the content is simply visible — a broken observer can't blank the page.
 * 2. Elements already in view on mount are never hidden, so nothing above the
 *    fold flashes in after paint.
 * 3. A {@link FAILSAFE_MS} timer forces visibility even if the observer never
 *    fires (detached nodes, `display:none` ancestors, zero-height containers).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(): UseRevealResult<T> {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    // Already on screen: leave it alone rather than animate it in late.
    if (el.getBoundingClientRect().top < (window.innerHeight || 0)) return;

    setShown(false);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    observer.observe(el);

    const failsafe = setTimeout(() => setShown(true), FAILSAFE_MS);

    return () => {
      clearTimeout(failsafe);
      observer.disconnect();
    };
  }, []);

  return { ref, shown };
}
